import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, ButtonGroup, Card, Form, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getFinanceSummary } from "../../../services/financeApi";
import "../../../css/FinanceDashboard.css";

const money = (value) => {
  if (value === null || value === undefined) return "—";
  const number = Number(value);
  const sign = number < 0 ? "-" : "";
  return `${sign}₹${Math.abs(number).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

/** Lakhs and crores, because a rupee axis in full is unreadable. */
const shortMoney = (value) => {
  const number = Number(value || 0);
  const sign = number < 0 ? "-" : "";
  const size = Math.abs(number);
  if (size >= 10000000) return `${sign}₹${(size / 10000000).toFixed(1)}Cr`;
  if (size >= 100000) return `${sign}₹${(size / 100000).toFixed(1)}L`;
  if (size >= 1000) return `${sign}₹${Math.round(size / 1000)}k`;
  return `${sign}₹${size}`;
};

const percent = (value) =>
  value === null || value === undefined ? "—" : `${Number(value).toFixed(1)}%`;

const ratio = (value) =>
  value === null || value === undefined ? "—" : `${Number(value).toFixed(2)}×`;

const signedMoney = (value) => {
  if (value === null || value === undefined) return money(value);
  const number = Number(value);
  return number > 0 ? `+${money(number)}` : money(number);
};

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const lastMonths = (count) => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - (count - 1), 1);
  return { fromMonth: monthKey(start), toMonth: monthKey(now) };
};

const singleMonth = (month) => ({ fromMonth: month, toMonth: month });

const thisYear = () => {
  const now = new Date();
  return { fromMonth: `${now.getFullYear()}-01`, toMonth: monthKey(now) };
};

// Fixed slots, assigned to the entity and never to its rank, so a range that changes the
// months on screen never repaints a series.
const SERIES = [
  { key: "revenue", name: "Revenue", color: "var(--series-revenue)" },
  { key: "totalCost", name: "Total cost", color: "var(--series-cost)" },
  { key: "netProfit", name: "Net profit", color: "var(--series-profit)" },
];

/** "Sep 2026" is too wide to repeat twelve times across a phone. */
const shortLabel = (label) => String(label || "").replace(/ (\d\d)(\d\d)$/, " '$2");

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="recharts-default-tooltip p-2">
      <div className="fw-semibold small mb-1">{label}</div>
      {payload.map((item) => (
        <div key={item.dataKey} className="d-flex justify-content-between gap-3 small">
          <span className="text-muted">
            <span className="swatch d-inline-block me-1"
              style={{ width: 8, height: 8, borderRadius: 2, background: item.fill }} />
            {item.name}
          </span>
          <span className="fw-semibold">{money(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function FinanceDashboard() {
  const [range, setRange] = useState(() => lastMonths(6));
  // "month" picks one month with a single control; "range" keeps the From / To pair.
  const [mode, setMode] = useState("range");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSummary(await getFinanceSummary(range));
    } catch (error) {
      toast.error(errorMessage(error, "Could not load the profit report"));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const patchRange = (field, value) => setRange((prev) => ({ ...prev, [field]: value }));

  const showMonth = (month) => {
    if (!month) return;
    setMode("month");
    setRange(singleMonth(month));
  };

  const showRange = (next) => {
    setMode("range");
    setRange(next);
  };

  const chartData = useMemo(() => (summary?.byMonth || []).map((row) => ({
    label: shortLabel(row.label) || row.month,
    revenue: Number(row.revenue || 0),
    totalCost: Number(row.directCost || 0) + Number(row.operatingCost || 0),
    netProfit: Number(row.netProfit || 0),
  })), [summary]);

  const totals = summary?.totals;
  const hasReconciliation = totals?.configuredProfit !== undefined;

  return (
    <Card className="shadow-sm io-finance-chart">
      <Card.Header>
        <div className="fw-semibold">Profit &amp; ROI</div>
        <div className="small text-muted">
          Revenue counts in the month the booking was taken, so the advertising and salary that
          won it sit beside it. Hotel, transport, vendor and cashback are the direct cost of the
          trip; salary, incentive, advertising and the bills come from the expense ledger.
        </div>
      </Card.Header>
      <Card.Body>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-lg-2 col-md-4">
            <Form.Label className="small d-block">View</Form.Label>
            <ButtonGroup size="sm" className="w-100">
              <Button variant={mode === "month" ? "primary" : "outline-primary"}
                onClick={() => showMonth(range.toMonth)}>Month</Button>
              <Button variant={mode === "range" ? "primary" : "outline-primary"}
                onClick={() => setMode("range")}>Range</Button>
            </ButtonGroup>
          </div>
          {mode === "month" ? (
            <div className="col-lg-2 col-md-4">
              <Form.Label className="small">Month</Form.Label>
              <Form.Control size="sm" type="month" value={range.toMonth}
                onChange={(e) => showMonth(e.target.value)} />
            </div>
          ) : (
            <>
              <div className="col-lg-2 col-md-4">
                <Form.Label className="small">From month</Form.Label>
                <Form.Control size="sm" type="month" value={range.fromMonth}
                  onChange={(e) => patchRange("fromMonth", e.target.value)} />
              </div>
              <div className="col-lg-2 col-md-4">
                <Form.Label className="small">To month</Form.Label>
                <Form.Control size="sm" type="month" value={range.toMonth}
                  onChange={(e) => patchRange("toMonth", e.target.value)} />
              </div>
            </>
          )}
          <div className="col-lg d-flex flex-wrap gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => showMonth(monthKey(new Date()))}>This Month</Button>
            <Button size="sm" variant="outline-secondary"
              onClick={() => {
                const now = new Date();
                showMonth(monthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
              }}>Last Month</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => showRange(lastMonths(3))}>Last 3 Months</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => showRange(lastMonths(6))}>Last 6 Months</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => showRange(lastMonths(12))}>Last 12 Months</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => showRange(thisYear())}>This Year</Button>
          </div>
        </div>

        {loading ? (
          <div className="text-muted"><Spinner size="sm" className="me-2" />Loading the profit report...</div>
        ) : !summary ? (
          <div className="text-muted">Nothing to report for this range.</div>
        ) : (
          <>
            <div className="row g-2 mb-3">
              <div className="col-md-3 col-6">
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Revenue</div>
                  <div className="fs-5 fw-semibold">{money(totals.revenue)}</div>
                  <div className="small text-muted">
                    {totals.bookings} bookings · {totals.travellers} travellers
                  </div>
                  {hasReconciliation && (
                    <div className="small text-muted">
                      {totals.adults} adults / {totals.kids} kids / {totals.chargeablePersons} chargeable
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Gross margin</div>
                  <div className="fs-5 fw-semibold">{money(totals.grossMargin)}</div>
                  <div className="small text-muted">
                    {percent(totals.grossMarginPercent)} after trip costs
                  </div>
                  <div className="small text-muted">
                    Cashback paid: <span className="fw-semibold">{money(totals.cashbackCost)}</span>
                  </div>
                  {Number(totals.customizationCharges || 0) > 0 && (
                    <div className="small text-muted" title="Charged to customers on top of the package and passed on to vendors - not profit">
                      Customization to vendors: <span className="fw-semibold">{money(totals.customizationCharges)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Net profit</div>
                  <div className={`fs-5 fw-semibold${Number(totals.netProfit) < 0 ? " io-finance-loss" : ""}`}>
                    {money(totals.netProfit)}
                  </div>
                  <div className="small text-muted">{percent(totals.netMarginPercent)} of revenue</div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Return on spend</div>
                  <div className="fs-5 fw-semibold">{percent(totals.roiPercent)}</div>
                  <div className="small text-muted">Profit against everything spent</div>
                </div>
              </div>
            </div>

            <FinanceExplainer totals={totals} />

            {hasReconciliation && (
              <section className="io-finance-reconciliation mb-3" aria-labelledby="margin-reconciliation-heading">
                <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-2">
                  <div>
                    <div id="margin-reconciliation-heading" className="fw-semibold">Margin reconciliation</div>
                    <div className="small text-muted">
                      The same gross margin, built up from the profit set in Dynamic Pricing
                    </div>
                  </div>
                  <div className="small text-muted">
                    {totals.reconciledBookings} of {totals.bookings} bookings reconciled
                  </div>
                </div>
                <div className="io-finance-reconciliation-grid">
                  <ReconciliationMetric label="Configured profit" value={money(totals.configuredProfit)}
                    hint={`Profit set per trip in Dynamic Pricing, added to every quote · ${totals.chargeablePersons} chargeable people`} />
                  <ReconciliationMetric label="Hotel margin" value={signedMoney(totals.hotelMargin)}
                    hint="Hotel customer price above what the hotel is paid" />
                  <ReconciliationMetric label="Price rounding"
                    value={signedMoney(totals.roundingAdjustment)}
                    hint="Quotes are rounded up to the next ₹100" />
                  <ReconciliationMetric label="Booking price changes"
                    value={signedMoney(totals.priceAdjustment)}
                    hint={`Price changed after the quote - discounts, downgrades${Number(totals.customizationCharges || 0) > 0
                      ? `. Leaves out ${money(totals.customizationCharges)} customization, passed on to vendors`
                      : ""}`} />
                  <ReconciliationMetric label="Final trip cost changes"
                    value={signedMoney(-Number(totals.directCostVariance || 0))}
                    hint="Hotel, transport and vendor costs below (+) or above (−) the quote" />
                  {Number(totals.unreconciledBookings || 0) > 0 && (
                    <ReconciliationMetric label="Unreconciled booking margin"
                      value={signedMoney(totals.unreconciledMargin)}
                      hint={`${totals.unreconciledBookings} booking snapshots need review`}
                      warning />
                  )}
                  <ReconciliationMetric label="Gross margin" value={money(totals.grossMargin)} total
                    hint="All of the above added up" />
                </div>
              </section>
            )}

            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} barGap={2} margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
                    tickLine={false} axisLine={{ stroke: "var(--chart-grid)" }} interval={0} />
                  <YAxis tickFormatter={shortMoney} width={58}
                    tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
                    tickLine={false} axisLine={false} />
                  <ReferenceLine y={0} stroke="var(--chart-axis)" strokeWidth={1} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "var(--chart-grid)", opacity: 0.35 }} />
                  {SERIES.map((series) => (
                    <Bar key={series.key} dataKey={series.key} name={series.name}
                      fill={series.color} radius={[4, 4, 0, 0]} maxBarSize={26} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="io-finance-legend small text-muted mt-2 mb-4">
              {SERIES.map((series) => (
                <span key={series.key}>
                  <span className="swatch" style={{ background: series.color }} />
                  {series.name}
                </span>
              ))}
            </div>

            <div className="table-responsive mb-4">
              <Table bordered hover size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-end">Bookings</th>
                    <th className="text-end">Revenue</th>
                    <th className="text-end">Trip cost</th>
                    <th className="text-end" title="Already included in trip cost">Cashback paid</th>
                    <th className="text-end" title="Customization charged to customers and still owed to vendors - already included in trip cost">Customization owed</th>
                    {hasReconciliation && <th className="text-end">Configured profit</th>}
                    {hasReconciliation && <th className="text-end" title="Customer price above hotel payable">Hotel margin</th>}
                    <th className="text-end">Gross margin</th>
                    <th className="text-end">Salary</th>
                    <th className="text-end">Incentive</th>
                    <th className="text-end">Ads</th>
                    <th className="text-end">Other</th>
                    <th className="text-end">Net profit</th>
                    <th className="text-end">Net %</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byMonth.map((row) => (
                    <tr key={row.month}>
                      <td className="fw-semibold">
                        {row.label}
                        {row.cancelledBookings > 0 && (
                          <div className="small text-muted fw-normal">
                            {row.cancelledBookings} cancelled
                          </div>
                        )}
                      </td>
                      <td className="text-end">{row.bookings}</td>
                      <td className="text-end">{money(row.revenue)}</td>
                      <td className="text-end">{money(row.directCost)}</td>
                      <td className="text-end">{money(row.cashbackCost)}</td>
                      <td className="text-end">{money(row.customizationCost)}</td>
                      {hasReconciliation && <td className="text-end">{money(row.configuredProfit)}</td>}
                      {hasReconciliation && <td className="text-end">{money(row.hotelMargin)}</td>}
                      <td className="text-end">{money(row.grossMargin)}</td>
                      <td className="text-end">{money(row.salaryCost)}</td>
                      <td className="text-end">{money(row.incentiveCost)}</td>
                      <td className="text-end">{money(row.adSpend)}</td>
                      <td className="text-end">{money(row.otherOperatingCost)}</td>
                      <td className={`text-end fw-semibold${Number(row.netProfit) < 0 ? " io-finance-loss" : ""}`}>
                        {money(row.netProfit)}
                      </td>
                      <td className="text-end">{percent(row.netMarginPercent)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-light fw-semibold">
                    <td>{totals.label}</td>
                    <td className="text-end">{totals.bookings}</td>
                    <td className="text-end">{money(totals.revenue)}</td>
                    <td className="text-end">{money(totals.directCost)}</td>
                    <td className="text-end">{money(totals.cashbackCost)}</td>
                    <td className="text-end">{money(totals.customizationCost)}</td>
                    {hasReconciliation && <td className="text-end">{money(totals.configuredProfit)}</td>}
                    {hasReconciliation && <td className="text-end">{money(totals.hotelMargin)}</td>}
                    <td className="text-end">{money(totals.grossMargin)}</td>
                    <td className="text-end">{money(totals.salaryCost)}</td>
                    <td className="text-end">{money(totals.incentiveCost)}</td>
                    <td className="text-end">{money(totals.adSpend)}</td>
                    <td className="text-end">{money(totals.otherOperatingCost)}</td>
                    <td className={`text-end${Number(totals.netProfit) < 0 ? " io-finance-loss" : ""}`}>
                      {money(totals.netProfit)}
                    </td>
                    <td className="text-end">{percent(totals.netMarginPercent)}</td>
                  </tr>
                </tfoot>
              </Table>
            </div>

            <div className="row g-2">
              <div className="col-lg-6">
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted mb-2">What it costs to win work</div>
                  <Metric label="Cost per lead" value={money(totals.costPerLead)}
                    hint={`${totals.leads} leads`} />
                  <Metric label="Cost per booking" value={money(totals.costPerBooking)}
                    hint="Ad spend divided by bookings" />
                  <Metric label="Return on ad spend" value={ratio(totals.returnOnAdSpend)}
                    hint="Revenue per rupee of advertising" />
                  <Metric label="Booking-to-lead ratio" value={percent(summary.leadToBookingPercent)}
                    hint="Bookings created / leads created in this range" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted mb-2">What the team produces</div>
                  <Metric label="Revenue per employee" value={money(summary.revenuePerEmployee)}
                    hint={`${summary.activeEmployees} currently active employees`} />
                  <Metric label="Gross margin per employee" value={money(summary.marginPerEmployee)} />
                  <Metric label="Salary as a share of revenue" value={percent(summary.salaryPercentOfRevenue)} />
                  <Metric label="Revenue per booking" value={money(totals.revenuePerBooking)}
                    hint={`${money(totals.marginPerBooking)} margin each`} />
                </div>
              </div>
            </div>

            {summary.operatingByHead?.length > 0 && (
              <div className="border rounded p-2 mt-2">
                <div className="small text-muted mb-2">
                  Running cost by head — a payment covering several months is spread across them
                </div>
                <div className="row g-1">
                  {summary.operatingByHead.map((head) => (
                    <div key={head.category} className="col-md-4 col-6">
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">{head.categoryName}</span>
                        <span className="fw-semibold">{money(head.total)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

function Metric({ label, value, hint }) {
  return (
    <div className="d-flex justify-content-between align-items-baseline border-bottom py-1">
      <div>
        <div className="small">{label}</div>
        {hint && <div className="small text-muted">{hint}</div>}
      </div>
      <div className="fw-semibold">{value}</div>
    </div>
  );
}

const EXPLAINER_OPEN_KEY = "financeExplainerOpen";

/**
 * The page's numbers in plain words, worked through with this range's own figures: what the trips
 * earned (gross margin), what is left after running the business (net profit), and the ratios.
 * Open until someone closes it; remembered on this computer.
 */
function FinanceExplainer({ totals }) {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(EXPLAINER_OPEN_KEY) !== "false";
    } catch {
      return true;
    }
  });
  const toggle = () => setOpen((current) => {
    try {
      localStorage.setItem(EXPLAINER_OPEN_KEY, String(!current));
    } catch {
      // Storage unavailable: it just opens again next time.
    }
    return !current;
  });
  const totalSpent = Number(totals.directCost || 0) + Number(totals.operatingCost || 0);
  const customizationOwed = Number(totals.customizationCost || 0);

  return (
    <section className="io-finance-explainer mb-3" aria-labelledby="finance-explainer-heading">
      <button type="button" className="io-finance-explainer-toggle" onClick={toggle} aria-expanded={open}>
        <span id="finance-explainer-heading" className="fw-semibold">How these numbers are worked out</span>
        <span className="small text-muted">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="io-finance-explainer-grid">
          <div>
            <div className="fw-semibold mb-1">Gross margin: what the trips earned</div>
            <StatementRow label="Revenue" value={money(totals.revenue)}
              hint="Booking totals, in the month each booking was taken. Cancelled bookings are left out." />
            <StatementRow minus label="Hotel payables" value={money(totals.hotelCost)} />
            <StatementRow minus label="Transport" value={money(totals.transportCost)}
              hint="The agreed payable, or what was paid where none was agreed" />
            <StatementRow minus label="Vendors" value={money(totals.vendorCost)}
              hint="Breakfast, add-ons and outside vendors" />
            <StatementRow minus label="Cashback paid" value={money(totals.cashbackCost)} />
            {customizationOwed > 0 && (
              <StatementRow minus label="Customization owed to vendors" value={money(totals.customizationCost)}
                hint="Customization added to a price is passed on to vendors - it is not profit" />
            )}
            <StatementRow total label="Gross margin" value={money(totals.grossMargin)}
              hint={`${percent(totals.grossMarginPercent)} of revenue`} />
          </div>
          <div>
            <div className="fw-semibold mb-1">Net profit: after running the business</div>
            <StatementRow label="Gross margin" value={money(totals.grossMargin)} />
            <StatementRow minus label="Salary" value={money(totals.salaryCost)} />
            <StatementRow minus label="Incentive" value={money(totals.incentiveCost)} />
            <StatementRow minus label="Advertising" value={money(totals.adSpend)} />
            <StatementRow minus label="Other running costs" value={money(totals.otherOperatingCost)}
              hint="Rent, software, bills and the rest of the Expenses ledger" />
            <StatementRow total label="Net profit" value={money(totals.netProfit)}
              hint={`${percent(totals.netMarginPercent)} of revenue`} />
            <div className="small text-muted mt-1">
              Running costs come from Expenses, spread over the months each payment covers.
            </div>
          </div>
          <div>
            <div className="fw-semibold mb-1">Return on spend</div>
            <StatementRow label="Net profit" value={money(totals.netProfit)} />
            <StatementRow label="÷ Everything spent" value={money(totalSpent)}
              hint={`Trip costs ${money(totals.directCost)} + running costs ${money(totals.operatingCost)}`} />
            <StatementRow total label="Return on spend" value={percent(totals.roiPercent)}
              hint="Profit made for every ₹100 spent" />
            <div className="small text-muted mt-2">
              The <span className="fw-semibold">Margin reconciliation</span> below reaches the same gross margin another
              way: from the profit set in Dynamic Pricing, plus what changed after the quote.
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function StatementRow({ label, value, hint, minus = false, total = false }) {
  return (
    <div className={`io-finance-statement-row${total ? " is-total" : ""}`}>
      <div className="d-flex justify-content-between gap-2">
        <span className={minus ? "text-muted" : undefined}>{minus ? "− " : total ? "= " : ""}{label}</span>
        <span className={total ? "fw-semibold" : undefined}>{value}</span>
      </div>
      {hint && <div className="small text-muted">{hint}</div>}
    </div>
  );
}

function ReconciliationMetric({ label, value, hint, total = false, warning = false }) {
  return (
    <div className={`io-finance-reconciliation-item${total ? " is-total" : ""}`}>
      <div className={`small${warning ? " io-finance-loss" : " text-muted"}`}>{label}</div>
      <div className="fw-semibold">{value}</div>
      {hint && <div className={`small${warning ? " io-finance-loss" : " text-muted"}`}>{hint}</div>}
    </div>
  );
}
